import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";
import { useTranslation, T } from "@/shared/i18n";
import { getCachedAuth } from "@/shared/lib/auth";
import { H2, Text } from "@/shared/ui/typography";
import { Button } from "@/shared/ui/button";
import { useFetchSharedTenderQuery } from "@/features/share-tender/api/use-fetch-shared-tender-query";
import { AnnouncementTypeValue } from "@/shared/gql/graphql";
import { SharedTenderHeader } from "./-shared-tender-header";
import {
  SummarySection,
  TenderDetailsSection,
  LotsSection,
  DocumentsSection,
  AgendaSection,
  ContractSection,
  RectificationSection,
  AuthoritySection,
  RelatedPublicationsSection,
  ContactSection,
} from "./-shared-tender-sections";
import { TEXT_LINK_CLASSES } from "@/shared/ui/text-link";
import { cn } from "@/shared/lib/utils";

const TENDER_APP_URL =
  import.meta.env.VITE_MARKETING_URL ?? "https://tender.app";

const SharedTenderPage = () => {
  const { token } = Route.useParams();
  const { t } = useTranslation();
  const { sharedTender, loading, error } = useFetchSharedTenderQuery(token);
  const isAuthenticated = !!getCachedAuth();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2Icon className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  const isExpired = sharedTender?.reason === "expired";
  const hasNoTender = error || !sharedTender?.tender;

  if (hasNoTender) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <H2 className="mb-2">
          {isExpired ? (
            <T id="This link has expired" />
          ) : (
            <T id="This link is no longer available" />
          )}
        </H2>
        {!isAuthenticated && !sharedTender?.reason ? (
          <>
            <Text className="text-neutral-500 mb-6">
              <T id="This tender may require you to log in. Please sign in and try again." />
            </Text>
            <Button asChild>
              <a
                href={`/auth?redirect=${encodeURIComponent(`/share/tender/${token}`)}`}
              >
                <T id="Sign in" />
              </a>
            </Button>
          </>
        ) : (
          <>
            <Text className="text-neutral-500 mb-6">
              {isExpired ? (
                <T id="The shared tender link has expired and is no longer accessible." />
              ) : (
                <T id="The shared tender link may have expired or been revoked." />
              )}
            </Text>
            <a
              href={TENDER_APP_URL}
              className={cn(TEXT_LINK_CLASSES, "underline")}
            >
              {t("Go to tender.app")}
            </a>
          </>
        )}
      </div>
    );
  }

  const tender = sharedTender.tender!;
  const sections = sharedTender.sections ?? [];
  const { message, sharedBy, expiresAt } = sharedTender;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <SharedTenderHeader
        tender={tender}
        sharedBy={sharedBy}
        expiresAt={expiresAt}
        message={message}
      />

      <div className="mt-8 flex flex-col gap-8">
        {sections.includes("summary") && <SummarySection tender={tender} />}
        {sections.includes("summary") && (
          <TenderDetailsSection tender={tender} />
        )}
        {sections.includes("lots") && tender.lots.length > 1 && (
          <LotsSection lots={tender.lots} />
        )}
        {sections.includes("documents") && (
          <DocumentsSection files={tender.files} token={token} />
        )}
        {sections.includes("agenda") && (
          <AgendaSection agenda={tender.agenda} />
        )}
        {sections.includes("contract") && (
          <ContractSection
            contracts={tender.contracts}
            isSingleLot={tender.lots.length <= 1}
          />
        )}
        {sections.includes("rectification") && (
          <RectificationSection rectifications={tender.rectifications} />
        )}
        {sections.includes("authority") && (
          <AuthoritySection
            publicAuthority={tender.publicAuthority}
            cooperationAuthorities={tender.cooperationAuthorities}
          />
        )}
        {sections.includes("contact") &&
          tender.announcementType?.value === AnnouncementTypeValue.Tender && (
            <ContactSection publicAuthority={tender.publicAuthority} />
          )}
        {sections.includes("related-publications") &&
          tender.relatedPublications.length > 0 && (
            <RelatedPublicationsSection
              publications={tender.relatedPublications}
            />
          )}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/share/tender/$token")({
  component: SharedTenderPage,
});
